/**
 * Componente de Notificações na Toolbar
 *
 * Exibe a campainha com badge de contagem e dropdown de notificações.
 * Integrado com NotificationStore para dados reais via WebSocket.
 *
 * @module core/layouts/components/toolbar/toolbar-notifications
 */

import { Component, inject, computed, ChangeDetectionStrategy } from '@angular/core';import { DIALOG_PRESETS } from '../../../../../shared/utils/dialog-presets';

import { Router } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { NotificationStore } from '../../../../notifications/services/notification.store';
import { navigateToNotification } from '../../../../notifications/utils/notification-routing';
import { NotificationSettingsDialog } from './notification-settings-dialog';
import {
  ProximityAlert,
  PRIORITY_CONFIG,
  ALERT_TYPE_ICON,
  NotificationPriority,
  NotificationType,
} from '../../../../notifications/models/notification.model';

@Component({
  selector: 'app-toolbar-notifications',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatBadgeModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  templateUrl: './toolbar-notifications.html',
  styleUrl: './toolbar-notifications.scss',
})
export class ToolbarNotifications {
  private readonly store = inject(NotificationStore);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  readonly unreadCount = this.store.unreadCount;
  readonly hasUnread = this.store.hasUnread;
  readonly status = this.store.notificationStatus;

  readonly displayNotifications = computed(() => {
    const all = this.store.notifications();
    const unread = all.filter(n => !n.read);
    const read = all.filter(n => n.read);
    return [...unread, ...read];
  });

  readonly bellIcon = computed(() =>
    this.status() === 'offline' ? 'notifications_off' : 'notifications'
  );

  readonly connectionTooltip = computed(() => {
    switch (this.status()) {
      case 'monitoring': return 'Monitorando alertas por proximidade';
      case 'push-active': return 'Notificações push ativas';
      case 'partial': return 'Conectado ao servidor';
      case 'offline': return 'Notificações desativadas';
    }
  });

  getAlertIcon(notification: ProximityAlert): string {
    if (!notification.alertType) {
      return 'notifications';
    }
    return ALERT_TYPE_ICON[notification.alertType] || 'notifications';
  }

  getPriorityConfig(priority?: NotificationPriority) {
    if (!priority) {
      return PRIORITY_CONFIG[NotificationPriority.MEDIUM];
    }
    return PRIORITY_CONFIG[priority] || PRIORITY_CONFIG[NotificationPriority.MEDIUM];
  }

  getAlertTypeLabel(type?: NotificationType): string {
    if (!type) {
      return 'Alerta';
    }
    const labels: Record<string, string> = {
      [NotificationType.ALERTA_DESAPARECIDO]: 'Desaparecido',
      [NotificationType.ALERTA_PROCURADO]: 'Procurado',
      [NotificationType.ALERTA_VEICULO]: 'Veículo',
      [NotificationType.ALERTA_PROPRIETARIO_PROCURADO]: 'Proprietário',
      [NotificationType.CHAMADO_190]: 'Chamado 190',
      [NotificationType.DESPACHO_EMERGENCIA]: 'Despacho',
      [NotificationType.AVISO_OPERACIONAL]: 'Operacional',
    };
    return labels[type] || type;
  }

  markAsRead(id: string): void {
    this.store.markAsRead(id);
  }

  markAllAsRead(): void {
    this.store.markAllAsRead();
  }

  openNotification(notification: ProximityAlert): void {
    navigateToNotification(this.router, notification, this.store);
  }

  openSettings(): void {
    this.dialog.open(NotificationSettingsDialog, { ...DIALOG_PRESETS.formCompact, 
      width: '460px',
      autoFocus: false,
    });
  }
}
